import React, { useState, useEffect, useRef } from 'react';
import { 
  ShoppingCart, 
  Search, 
  Filter, 
  CheckCircle, 
  XCircle, 
  Clock,
  Package,
  Eye,
  AlertTriangle,
  Truck,
  CheckCircle2,
  X,
  ArrowRight,
  Bell,
  Users,
  RefreshCw,
  UserCheck,
  UserX
} from 'lucide-react';
import io from 'socket.io-client';
import { API_BASE_URL } from '../api';
import './AdminOrders.css';

const formatCurrency = (value) => {
  const numeric = Number(value || 0);
  return `$${numeric.toFixed(2)}`;
};

const formatDateTime = (value) => {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString();
  } catch (err) {
    return value;
  }
};

const formatTitleCase = (value) => {
  if (!value) return '';
  const str = String(value).replace(/_/g, ' ').toLowerCase();
  return str.replace(/\b\w/g, (char) => char.toUpperCase());
};

const ORDER_COLUMNS = [
  { key: 'order_number', label: 'Order #' },
  { key: 'vendor_name', label: 'Vendor' },
  { key: 'customer_name', label: 'Customer' },
  { key: 'customer_phone', label: 'Phone' },
  { key: 'shipping_address', label: 'Address' },
  { key: 'payment_status', label: 'Payment', format: formatTitleCase },
  { key: 'payment_mode', label: 'Payment Mode', format: formatTitleCase },
  { key: 'item_count', label: 'Items', format: (value) => Number(value || 0) },
  { key: 'total_quantity', label: 'Qty', format: (value) => Number(value || 0) },
  { key: 'total_amount', label: 'Total', format: formatCurrency },
  { key: 'created_at', label: 'Created', format: formatDateTime }
];

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [selectedVendorFilter, setSelectedVendorFilter] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showDetailsFor, setShowDetailsFor] = useState(null);
  const [orderDetails, setOrderDetails] = useState({});
  const [socket, setSocket] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [reassignModal, setReassignModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState('');
  const [reassignReason, setReassignReason] = useState('');
  const [cancelModal, setCancelModal] = useState(false);
  const [refundModal, setRefundModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const socketRef = useRef(null);
  const [nowTs, setNowTs] = useState(Date.now());
  const [reassignedIds, setReassignedIds] = useState(new Set());
  const [paginationInfo, setPaginationInfo] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Fetch all orders (admin view)
  const fetchOrders = async (pageParam = currentPage, options = {}) => {
    try {
      setLoading(true);
      const apiKey = localStorage.getItem('adminApiKey') || 'dev-admin-key';
      const params = new URLSearchParams();
      if (filter !== 'all' && filter) params.append('status', filter);
      if (selectedVendorFilter) params.append('vendor_id', selectedVendorFilter);
      if (paymentStatusFilter) params.append('payment_status', paymentStatusFilter);
      if (searchQuery) params.append('search', searchQuery);
      if (dateFrom && dateTo) { params.append('date_from', dateFrom); params.append('date_to', dateTo); }
      const pageToFetch = typeof options.page === 'number'
        ? options.page
        : (typeof pageParam === 'number' ? pageParam : 1);
      const limitToUse = typeof options.limit === 'number' ? options.limit : rowsPerPage;
      params.append('page', String(pageToFetch));
      params.append('limit', String(limitToUse));

      const response = await fetch(`/api/admin/orders?${params.toString()}`, {
        headers: {
          'x-admin-key': apiKey
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.orders)
            ? data.orders
            : [];
        setOrders(list);
        if (Array.isArray(data)) {
          setPaginationInfo(null);
          setCurrentPage(pageToFetch);
        } else if (data?.pagination) {
          setPaginationInfo(data.pagination);
          const serverPage = parseInt(data.pagination.current_page, 10);
          if (!Number.isNaN(serverPage) && serverPage > 0) {
            setCurrentPage(serverPage);
          } else {
            setCurrentPage(pageToFetch);
          }
        } else {
          setPaginationInfo(null);
          setCurrentPage(pageToFetch);
        }
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrderDetails = async (orderId) => {
    try {
      const apiKey = localStorage.getItem('adminApiKey') || 'dev-admin-key';
      const res = await fetch(`/api/admin/orders/${orderId}`, {
        headers: { 'x-admin-key': apiKey }
      });
      if (res.ok) {
        const data = await res.json();
        setOrderDetails(prev => ({ ...prev, [orderId]: data }));
      }
    } catch (e) {
      // noop
    }
  };

  // Fetch vendors for reassignment
  const fetchVendors = async () => {
    try {
      const apiKey = localStorage.getItem('adminApiKey') || 'dev-admin-key';
      const response = await fetch('/api/admin/vendors', {
        headers: {
          'x-admin-key': apiKey
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setVendors(data.filter(vendor => vendor.status === 'APPROVED'));
      }
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  // WebSocket connection for real-time updates
  useEffect(() => {
    const apiKey = localStorage.getItem('adminApiKey') || 'dev-admin-key';
    if (apiKey) {
      const socketUrl = API_BASE_URL || window.location.origin;
      const newSocket = io(socketUrl, {
        auth: { token: apiKey, type: 'admin' }
      });
      
      newSocket.on('connect', () => {
        console.log('Admin connected to WebSocket');
        newSocket.emit('join-admin');
      });
      
      newSocket.on('order-update', (orderData) => {
        console.log('Admin order update received:', orderData);
        
        // Add notification for important updates
        if (orderData.action === 'status_updated' || orderData.action === 'reassigned') {
          const message = orderData.action === 'reassigned' 
            ? `Order ${orderData.order_number} reassigned to new vendor`
            : `Order ${orderData.order_number} status updated to ${orderData.status}`;
          
          setNotifications(prev => [...prev, {
            id: Date.now(),
            type: 'order_update',
            message,
            orderData,
            timestamp: new Date()
          }]);
        }
        
        // Update orders list
        if (orderData.action === 'reassigned') {
          setOrders(prev => {
            const exists = prev.some(o => o.id === orderData.id);
            if (exists) {
              return prev.map(order => order.id === orderData.id ? { ...order, ...orderData } : order);
            }
            return [orderData, ...prev];
          });
          setReassignedIds(prev => {
            const next = new Set(prev);
            next.add(orderData.id);
            return next;
          });
          // Auto-clear highlight after 10 minutes
          setTimeout(() => {
            setReassignedIds(prev => {
              const next = new Set(prev);
              next.delete(orderData.id);
              return next;
            });
          }, 10 * 60 * 1000);
        } else {
          setOrders(prev => prev.map(order => 
            order.id === orderData.id ? { ...order, ...orderData } : order
          ));
        }
      });
      
      setSocket(newSocket);
      socketRef.current = newSocket;
    }
    
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, []);

  // Polling fallback when socket is not connected
  useEffect(() => {
    const interval = setInterval(() => {
      const connected = !!socketRef.current && socketRef.current.connected;
      if (!connected) {
        fetchOrders(currentPage);
      }
    }, 30 * 1000);
    return () => clearInterval(interval);
  }, [currentPage]);

  // Tick timer to refresh SLA countdowns
  useEffect(() => {
    const interval = setInterval(() => setNowTs(Date.now()), 30 * 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    fetchOrders(1);
    fetchVendors();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
    fetchOrders(1);
  }, [filter, selectedVendorFilter, paymentStatusFilter, dateFrom, dateTo]);

  // Reassign order to different vendor
  const reassignOrder = async () => {
    if (!selectedOrder || !selectedVendor) {
      alert('Please select a vendor for reassignment');
      return;
    }

    try {
      const apiKey = localStorage.getItem('adminApiKey') || 'dev-admin-key';
      const response = await fetch(`/api/admin/orders/${selectedOrder.id}/reassign`, {
        method: 'PUT',
        headers: {
          'x-admin-key': apiKey,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          vendor_id: selectedVendor,
          reason: reassignReason
        })
      });
      
      if (response.ok) {
        setReassignModal(false);
        setSelectedOrder(null);
        setSelectedVendor('');
        setReassignReason('');
        fetchOrders(currentPage);
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to reassign order');
      }
    } catch (error) {
      console.error('Error reassigning order:', error);
      alert('Failed to reassign order');
    }
  };

  // Update order status (admin override)
  const updateOrderStatus = async (orderId, status) => {
    try {
      const apiKey = localStorage.getItem('adminApiKey') || 'dev-admin-key';
      const response = await fetch(`/api/admin/orders/${orderId}/status`, {
        method: 'PUT',
        headers: {
          'x-admin-key': apiKey,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status })
      });
      
      if (response.ok) {
        fetchOrders(currentPage);
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to update order status');
      }
    } catch (error) {
      console.error('Error updating order status:', error);
    }
  };

  const dismissNotification = (notificationId) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
  };

  const confirmCancel = async () => {
    if (!selectedOrder) return;
    try {
      const apiKey = localStorage.getItem('adminApiKey') || 'dev-admin-key';
      const res = await fetch(`/api/admin/orders/${selectedOrder.id}/cancel`, {
        method: 'PUT',
        headers: { 'x-admin-key': apiKey, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: cancelReason })
      });
      if (res.ok) {
        setCancelModal(false);
        setCancelReason('');
        setSelectedOrder(null);
        fetchOrders(currentPage);
      } else {
        const e = await res.json();
        alert(e.error || 'Failed to cancel order');
      }
    } catch (e) {
      console.error('Cancel order error', e);
    }
  };

  const confirmRefund = async () => {
    if (!selectedOrder) return;
    try {
      const apiKey = localStorage.getItem('adminApiKey') || 'dev-admin-key';
      const res = await fetch(`/api/admin/orders/${selectedOrder.id}/refund`, {
        method: 'PUT',
        headers: { 'x-admin-key': apiKey, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: refundReason })
      });
      if (res.ok) {
        setRefundModal(false);
        setRefundReason('');
        setSelectedOrder(null);
        fetchOrders(currentPage);
      } else {
        const e = await res.json();
        alert(e.error || 'Failed to refund order');
      }
    } catch (e) {
      console.error('Refund order error', e);
    }
  };

  const getStatusBadge = (status, slaStatus) => {
    const statusClasses = {
      'placed': slaStatus === 'sla_breached' ? 'status-sla-breached' : 
               slaStatus === 'sla_warning' ? 'status-sla-warning' : 'status-placed',
      'confirmed': 'status-confirmed',
      'packing': 'status-packing',
      'ready': 'status-ready',
      'out_for_delivery': 'status-out-for-delivery',
      'delivered': 'status-delivered',
      'cancelled': 'status-cancelled',
      'rejected': 'status-rejected',
      'reassigned': 'status-reassigned'
    };
    return statusClasses[status] || 'status-default';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'placed': return <Clock size={16} />;
      case 'confirmed': return <CheckCircle size={16} />;
      case 'packing': return <Package size={16} />;
      case 'ready': return <CheckCircle2 size={16} />;
      case 'out_for_delivery': return <Truck size={16} />;
      case 'delivered': return <CheckCircle2 size={16} />;
      case 'cancelled': return <XCircle size={16} />;
      case 'rejected': return <X size={16} />;
      default: return <Clock size={16} />;
    }
  };

  const getStatusText = (status) => {
    const statusTexts = {
      'placed': 'Placed',
      'confirmed': 'Confirmed',
      'packing': 'Packing',
      'ready': 'Ready',
      'out_for_delivery': 'Out for Delivery',
      'delivered': 'Delivered',
      'cancelled': 'Cancelled',
      'rejected': 'Rejected',
      'reassigned': 'Reassigned'
    };
    return statusTexts[status] || status;
  };

  const formatRemaining = (deadline) => {
    const ms = new Date(deadline).getTime() - nowTs;
    if (Number.isNaN(ms)) return '';
    if (ms <= 0) return 'SLA Breached';
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `SLA: ${minutes}m ${seconds.toString().padStart(2, '0')}s left`;
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = order.order_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (order.vendor_name && order.vendor_name.toLowerCase().includes(searchQuery.toLowerCase())) ||
                         (order.shipping_address && order.shipping_address.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesFilter = filter === 'all' || order.status === filter;
    return matchesSearch && matchesFilter;
  });

  const pendingOrders = orders.filter(order => order.status === 'placed');
  const activeOrders = orders.filter(order => ['confirmed', 'packing', 'ready', 'out_for_delivery'].includes(order.status));
  const completedOrders = orders.filter(order => ['delivered', 'cancelled', 'rejected'].includes(order.status));
  const visibleOrders = activeTab === 'pending'
    ? pendingOrders
    : activeTab === 'active'
      ? activeOrders
      : activeTab === 'completed'
        ? completedOrders
        : filteredOrders;
  const totalOrders = paginationInfo?.total_orders ?? orders.length;
  const totalPages = paginationInfo?.total_pages ?? (orders.length > 0 ? 1 : 0);

  return (
    <div className="admin-orders">
      {/* Notifications */}
      {notifications.length > 0 && (
        <div className="notifications-container">
          {notifications.map(notification => (
            <div key={notification.id} className="notification">
              <Bell size={16} />
              <span>{notification.message}</span>
              <button onClick={() => dismissNotification(notification.id)}>
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="admin-orders-header">
        <div className="header-content">
          <h1>Order Management</h1>
          <p>Monitor and manage all orders across vendors with real-time updates</p>
        </div>
        <div className="header-actions">
          <div className="order-stats">
            <div className="stat-item">
              <span className="stat-number">{pendingOrders.length}</span>
              <span className="stat-label">Pending</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{activeOrders.length}</span>
              <span className="stat-label">Active</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{completedOrders.length}</span>
              <span className="stat-label">Completed</span>
            </div>
          </div>
          <button className="btn btn-secondary" onClick={() => fetchOrders(currentPage)}>
            <RefreshCw size={20} />
            Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="admin-orders-tabs">
        <button 
          className={`tab ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => setActiveTab('all')}
        >
          <Eye size={16} />
          All Orders ({orders.length})
        </button>
        <button 
          className={`tab ${activeTab === 'pending' ? 'active' : ''}`}
          onClick={() => setActiveTab('pending')}
        >
          <Bell size={16} />
          Pending ({pendingOrders.length})
        </button>
        <button 
          className={`tab ${activeTab === 'active' ? 'active' : ''}`}
          onClick={() => setActiveTab('active')}
        >
          <Package size={16} />
          Active ({activeOrders.length})
        </button>
        <button 
          className={`tab ${activeTab === 'completed' ? 'active' : ''}`}
          onClick={() => setActiveTab('completed')}
        >
          <CheckCircle size={16} />
          Completed ({completedOrders.length})
        </button>
      </div>

      <div className="admin-orders-filters">
        <div className="filters-left">
          <div className="search-container">
            <Search size={20} className="search-icon" />
            <input
              type="text"
              placeholder="Search orders, vendors, addresses..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setCurrentPage(1);
                  fetchOrders(1);
                }
              }}
              className="search-input"
            />
          </div>
          
          <div className="filter-group">
            <Filter size={20} />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Status</option>
              <option value="placed">Placed</option>
              <option value="confirmed">Confirmed</option>
              <option value="packing">Packing</option>
              <option value="ready">Ready</option>
              <option value="out_for_delivery">Out for Delivery</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          <div className="filter-group">
            <Filter size={20} />
            <select
              value={paymentStatusFilter}
              onChange={(e) => setPaymentStatusFilter(e.target.value)}
              className="filter-select"
            >
              <option value="">All Payments</option>
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
              <option value="refunded">Refunded</option>
            </select>
          </div>

          <div className="filter-group">
            <Filter size={20} />
            <select
              value={selectedVendorFilter}
              onChange={(e) => setSelectedVendorFilter(e.target.value)}
              className="filter-select"
            >
              <option value="">All Vendors</option>
              {vendors.map(v => (
                <option key={v.id} value={v.id}>{v.business_name}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="filters-right">
          <div className="date-range">
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            <span style={{ margin: '0 4px' }}>to</span>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>
          <div className="results-count">
            {totalOrders > 0
              ? `${visibleOrders.length} of ${totalOrders} orders`
              : '0 orders'}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading orders...</div>
      ) : (
        <div className="admin-orders-list" style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {ORDER_COLUMNS.map(({ key, label }) => (
                  <th key={key} style={{ textAlign: 'left', borderBottom: '1px solid #eee', padding: '8px', whiteSpace: 'nowrap' }}>
                    {label}
                  </th>
                ))}
                <th style={{ textAlign: 'left', borderBottom: '1px solid #eee', padding: '8px' }}>actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleOrders.map((order) => (
                <tr key={order.id} className={reassignedIds.has(order.id) ? 'order-reassigned' : ''}>
                  {ORDER_COLUMNS.map(({ key, format }) => (
                    <td key={key} style={{ borderBottom: '1px solid #f6f6f6', padding: '8px', verticalAlign: 'top' }}>
                      {format ? format(order[key], order) : (order[key] ?? '')}
                    </td>
                  ))}
                  <td style={{ borderBottom: '1px solid #f6f6f6', padding: '8px' }}>
                    <span className={`status-badge ${getStatusBadge(order.status, order.sla_status)}`} style={{ marginRight: 8 }}>
                      {getStatusIcon(order.status)}
                      {getStatusText(order.status)}
                    </span>
                    {order.status === 'placed' && (
                      <button 
                        className="btn btn-primary btn-sm"
                        onClick={() => {
                          setSelectedOrder(order);
                          setReassignModal(true);
                        }}
                        style={{ marginRight: 6 }}
                      >
                        <UserCheck size={16} />
                        Reassign
                      </button>
                    )}
                    {['confirmed', 'packing', 'ready', 'out_for_delivery'].includes(order.status) && (
                      <button 
                        className="btn btn-secondary btn-sm"
                        onClick={() => {
                          const nextStatus = getNextStatus(order.status);
                          if (nextStatus) {
                            updateOrderStatus(order.id, nextStatus);
                          }
                        }}
                        style={{ marginRight: 6 }}
                      >
                        <ArrowRight size={16} />
                        Update
                      </button>
                    )}
                    {['placed','confirmed','packing','ready','out_for_delivery'].includes(order.status) && (
                      <button 
                        className="btn btn-secondary btn-sm"
                        onClick={() => { setSelectedOrder(order); setCancelModal(true); }}
                        style={{ marginRight: 6 }}
                      >
                        <XCircle size={16} />
                        Cancel
                      </button>
                    )}
                    {order.payment_status !== 'refunded' && (
                      <button 
                        className="btn btn-secondary btn-sm"
                        onClick={() => { setSelectedOrder(order); setRefundModal(true); }}
                        style={{ marginRight: 6 }}
                      >
                        <CheckCircle2 size={16} />
                        Refund
                      </button>
                    )}
                    <button className="btn btn-secondary btn-sm" onClick={() => {
                      setShowDetailsFor(prev => prev === order.id ? null : order.id);
                      if (!orderDetails[order.id]) fetchOrderDetails(order.id);
                    }}>
                      <Eye size={16} />
                      Details
                    </button>
                    {order.sla_deadline && order.status === 'placed' && (
                      <div className="sla-info" style={{ marginTop: 6 }}>
                        <Clock size={12} />
                        <span>{formatRemaining(order.sla_deadline)}</span>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {showDetailsFor && orderDetails[showDetailsFor] && (
            <div className="order-items" style={{ marginTop: 16 }}>
              <div className="items-header">Products Ordered</div>
              {(orderDetails[showDetailsFor].items || []).map(it => (
                <div key={it.id} className="item-row">
                  <div className="item-name">{it.product_name || `#${it.product_id}`}</div>
                  <div className="item-qty">x{it.quantity}</div>
                  <div className="item-price">${Number(it.unit_price).toFixed(2)}</div>
                  <div className="item-total">${Number(it.unit_price * it.quantity).toFixed(2)}</div>
                </div>
              ))}
            </div>
          )}

          {visibleOrders.length === 0 && (
            <div className="empty-state">
              <ShoppingCart size={48} />
              <h3>No orders found</h3>
              <p>No orders match your current filters</p>
            </div>
          )}
          {visibleOrders.length > 0 && (
            <div className="pagination-container" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginTop: 16 }}>
              <div style={{ fontSize: 14, color: '#666' }}>
                Showing {visibleOrders.length} of {totalOrders} orders (page {currentPage} of {Math.max(totalPages, 1)})
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    if (currentPage > 1) {
                      const prevPage = currentPage - 1;
                      setCurrentPage(prevPage);
                      fetchOrders(prevPage);
                    }
                  }}
                  disabled={currentPage <= 1}
                >
                  Prev
                </button>
                {(() => {
                  const pages = [];
                  const maxButtons = 7;
                  if (totalPages <= maxButtons) {
                    for (let i = 1; i <= totalPages; i += 1) {
                      pages.push(i);
                    }
                  } else {
                    const setPages = new Set([1, totalPages, currentPage]);
                    for (let i = 1; i <= 2; i += 1) {
                      setPages.add(currentPage - i);
                      setPages.add(currentPage + i);
                    }
                    const sortedPages = Array.from(setPages)
                      .filter(page => page >= 1 && page <= totalPages)
                      .sort((a, b) => a - b);

                    let lastPage = 0;
                    sortedPages.forEach((page) => {
                      if (lastPage && page - lastPage > 1) {
                        pages.push('ellipsis');
                      }
                      pages.push(page);
                      lastPage = page;
                    });
                  }
                  return pages.map((page, index) => {
                    if (page === 'ellipsis') {
                      return (
                        <span key={`ellipsis-${index}`} style={{ padding: '4px 8px', color: '#999' }}>…</span>
                      );
                    }
                    return (
                      <button
                        key={`page-${page}`}
                        className={`btn btn-secondary btn-sm ${page === currentPage ? 'active' : ''}`}
                        style={page === currentPage ? { backgroundColor: '#2563eb', borderColor: '#2563eb', color: '#fff' } : {}}
                        onClick={() => {
                          if (page !== currentPage) {
                            setCurrentPage(page);
                            fetchOrders(page);
                          }
                        }}
                      >
                        {page}
                      </button>
                    );
                  });
                })()}
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    if (currentPage < totalPages) {
                      const nextPage = currentPage + 1;
                      setCurrentPage(nextPage);
                      fetchOrders(nextPage);
                    }
                  }}
                  disabled={currentPage >= totalPages || totalPages === 0}
                >
                  Next
                </button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 14, color: '#666' }}>Rows per page</span>
                <select
                  value={rowsPerPage}
                  onChange={(e) => {
                    const newSize = parseInt(e.target.value, 10) || 10;
                    setRowsPerPage(newSize);
                    setCurrentPage(1);
                    fetchOrders(1, { limit: newSize, page: 1 });
                  }}
                  style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #ddd' }}
                >
                  {[10, 20, 50, 100].map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Reassign Order Modal */}
      {reassignModal && selectedOrder && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Reassign Order</h2>
              <button className="close-btn" onClick={() => setReassignModal(false)}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>Order: <strong>{selectedOrder.order_number}</strong></p>
              <p>Current Vendor: <strong>{selectedOrder.vendor_name || 'Unknown'}</strong></p>
              <p>Amount: <strong>${selectedOrder.total_amount}</strong></p>
              
              <div className="form-group">
                <label>Select New Vendor</label>
                <select
                  value={selectedVendor}
                  onChange={(e) => setSelectedVendor(e.target.value)}
                  className="form-select"
                >
                  <option value="">Choose a vendor...</option>
                  {vendors.filter(v => v.id !== selectedOrder.vendor_id).map(vendor => (
                    <option key={vendor.id} value={vendor.id}>
                      {vendor.business_name} - {vendor.email}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label>Reason for Reassignment</label>
                <textarea
                  value={reassignReason}
                  onChange={(e) => setReassignReason(e.target.value)}
                  rows="3"
                  placeholder="Enter reason for reassignment..."
                  className="form-textarea"
                />
              </div>
            </div>
            <div className="modal-actions">
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={() => setReassignModal(false)}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="btn btn-primary" 
                onClick={reassignOrder}
                disabled={!selectedVendor}
              >
                Reassign Order
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Order Modal */}
      {cancelModal && selectedOrder && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Cancel Order</h2>
              <button className="close-btn" onClick={() => setCancelModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p>Order: <strong>{selectedOrder.order_number}</strong></p>
              <div className="form-group">
                <label>Reason for Cancellation</label>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  rows="3"
                  placeholder="Enter reason for cancellation..."
                  className="form-textarea"
                />
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setCancelModal(false)}>Close</button>
              <button type="button" className="btn btn-primary" onClick={confirmCancel}>Confirm Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Refund Order Modal */}
      {refundModal && selectedOrder && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Refund Payment</h2>
              <button className="close-btn" onClick={() => setRefundModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p>Order: <strong>{selectedOrder.order_number}</strong></p>
              <div className="form-group">
                <label>Reason for Refund</label>
                <textarea
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  rows="3"
                  placeholder="Enter reason for refund..."
                  className="form-textarea"
                />
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setRefundModal(false)}>Close</button>
              <button type="button" className="btn btn-primary" onClick={confirmRefund}>Confirm Refund</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper function to get next status
const getNextStatus = (currentStatus) => {
  const statusFlow = {
    'confirmed': 'packing',
    'packing': 'ready',
    'ready': 'out_for_delivery',
    'out_for_delivery': 'delivered'
  };
  return statusFlow[currentStatus] || '';
};

export default AdminOrders;
